import React, { useState, useEffect } from 'react';
import { Download, AlertCircle, CheckCircle, Loader2, Mail, MessageSquare, Sparkles, LayoutList, LayoutGrid, Archive as ArchiveIcon, Instagram, Facebook, Filter, Ban, Settings, Key } from 'lucide-react';
import ConversationList from '../components/conversations/ConversationList';
import MessageThread from '../components/conversations/MessageThread';
import EmptyState from '../components/conversations/EmptyState';
import Calendar from '../components/Calendar';
import { flooringBotDb } from '../lib/supabase';
import { ConversationImporter, ImportProgress } from '../services/ConversationImporter';
import { GmailAPI } from '../services/GmailAPI';
import { useAuthStore } from '../stores/authStore';

export default function ConversationsPage() {
  // Auth store for multi-tenant database persistence
  const { profile, updateProfile } = useAuthStore();

  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [importResult, setImportResult] = useState<{ success: boolean; message: string } | null>(null);
  const [gmailApi] = useState(() => new GmailAPI());
  const [gmailImporting, setGmailImporting] = useState(false);
  const [autoImportEnabled, setAutoImportEnabled] = useState(true);
  const [smsLastFetch, setSmsLastFetch] = useState<Date | null>(null);
  const [gmailLastFetch, setGmailLastFetch] = useState<Date | null>(null);
  const [smsFetching, setSmsFetching] = useState(false);
  const [gmailFetching, setGmailFetching] = useState(false);
  const [, setTick] = useState(0);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [layoutMode, setLayoutMode] = useState<'dropdown' | 'tabs'>('dropdown');
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [smartFilterEnabled, setSmartFilterEnabled] = useState(false);

  // Block rules with types: exact, domain, pattern
  type BlockRule = { type: 'exact' | 'domain' | 'pattern'; value: string };
  const [blockRules, setBlockRules] = useState<BlockRule[]>([]);

  // User's personal API key
  const [userApiKey, setUserApiKey] = useState<string>('');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);

  // Load API key from database (profile) or localStorage fallback
  useEffect(() => {
    if (profile?.preferences?.japlanchers_api_key) {
      // Authenticated user - load from database
      const dbKey = profile.preferences.japlanchers_api_key;
      console.log('[Multi-Tenant DB] Loaded API key from database for user:', profile.email, '-', dbKey.substring(0, 8) + '...');
      setUserApiKey(dbKey);
    } else {
      // Fallback to localStorage for non-authenticated users
      const storedKey = localStorage.getItem('japlanchers_api_key');
      if (storedKey) {
        console.log('[Multi-Tenant LocalStorage] Loaded API key from browser:', storedKey.substring(0, 8) + '...');
        setUserApiKey(storedKey);
      } else {
        console.log('[Multi-Tenant] No API key found - user needs to configure');
      }
    }
  }, [profile]);

  // Update "ago" timestamps every second
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load calendar events from Google Calendar
  useEffect(() => {
    loadCalendarEvents();
  }, []);

  const loadCalendarEvents = async () => {
    try {
      // Fetch from Google Calendar API (would need OAuth setup for real implementation)
      // For now, create mock events based on keywords
      const mockEvents = [
        {
          id: '1',
          title: 'Kitchen Floor Installation',
          start: new Date(2024, 11, 15, 9, 0),
          end: new Date(2024, 11, 15, 17, 0),
          type: 'job'
        },
        {
          id: '2',
          title: 'Free Estimate - Bathroom',
          start: new Date(2024, 11, 18, 14, 0),
          end: new Date(2024, 11, 18, 15, 0),
          type: 'estimate'
        },
        {
          id: '3',
          title: 'Follow-up Call - Smith',
          start: new Date(2024, 11, 20, 10, 0),
          end: new Date(2024, 11, 20, 10, 30),
          type: 'call'
        },
        {
          id: '4',
          title: 'Hardwood Install - Living Room',
          start: new Date(2024, 11, 22, 8, 0),
          end: new Date(2024, 11, 22, 16, 0),
          type: 'job'
        }
      ];

      setCalendarEvents(mockEvents);
    } catch (error) {
      console.error('Failed to load calendar events:', error);
    }
  };

  // Load conversations on mount
  useEffect(() => {
    loadConversations();

    // Handle Gmail OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      handleGmailCallback(code);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Subscribe to real-time updates
    const subscription = flooringBotDb.subscribeToConversations(() => {
      loadConversations(); // Reload when conversations change
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Auto-import new conversations every 5 seconds
  useEffect(() => {
    if (!autoImportEnabled || !userApiKey) {
      if (!userApiKey) {
        console.log('[Multi-Tenant] Auto-import disabled - no API key configured');
      }
      return;
    }

    const autoImport = async () => {
      // Use user's API key instead of environment variable
      if (!importing) {
        console.log('[Multi-Tenant] Auto-importing with user key:', userApiKey.substring(0, 8) + '...');
        setSmsFetching(true);
        try {
          const importer = new ConversationImporter(userApiKey);
          // Only fetch 2 most recent since we poll every 5s
          await importer.importRecentConversations(2);
          setSmsLastFetch(new Date());
        } catch (error) {
          console.error('[Multi-Tenant] Auto-import failed:', error);
        } finally {
          setSmsFetching(false);
        }
      }

      // Auto-import Gmail if authenticated
      if (gmailApi.isAuthenticated() && !gmailImporting) {
        setGmailFetching(true);
        try {
          const threads = await gmailApi.fetchRecentConversations(10);

          for (const thread of threads) {
            const firstEmail = thread[0];

            // Check if exists
            const { data: existing } = await flooringBotDb.supabase
              .from('conversations')
              .select('id')
              .eq('channel_id', firstEmail.threadId)
              .eq('channel', 'gmail')
              .single();

            if (existing) continue;

            // Import new thread (simplified)
            const { data: client } = await flooringBotDb.supabase
              .from('clients')
              .upsert({
                name: firstEmail.from.split('<')[0].trim() || firstEmail.from,
                email: firstEmail.from.match(/<(.+)>/)?.[1] || firstEmail.from
              }, { onConflict: 'email' })
              .select('id')
              .single();

            if (!client) continue;

            const { data: conversation } = await flooringBotDb.supabase
              .from('conversations')
              .insert({
                client_id: client.id,
                channel: 'gmail',
                channel_id: firstEmail.threadId,
                status: 'active',
                metadata: { subject: firstEmail.subject }
              })
              .select('id')
              .single();

            if (conversation) {
              const messages = thread.map(email => ({
                conversation_id: conversation.id,
                sender_type: email.from.includes(firstEmail.to) ? 'client' : 'user',
                content: email.body,
                external_id: email.id,
                metadata: { subject: email.subject, from: email.from, to: email.to, isRead: email.isRead },
                created_at: email.date.toISOString()
              }));

              await flooringBotDb.supabase.from('messages').insert(messages);
            }
          }

          setGmailLastFetch(new Date());
        } catch (error) {
          console.error('Auto-import Gmail failed:', error);
        } finally {
          setGmailFetching(false);
        }
      }

      // Reload if we imported anything
      await loadConversations();
    };

    // Run immediately on mount
    autoImport();

    // Then every 5 seconds
    const interval = setInterval(autoImport, 5000);

    return () => clearInterval(interval);
  }, [autoImportEnabled, importing, gmailImporting, userApiKey]);

  const loadConversations = async () => {
    setLoading(true);
    const data = await flooringBotDb.getConversations();
    setConversations(data);

    // Store latest conversation timestamp for incremental fetching
    if (data.length > 0) {
      const latest = data.reduce((max, conv) => {
        const convTime = new Date(conv.updatedAt || conv.createdAt).getTime();
        return convTime > max ? convTime : max;
      }, 0);
      localStorage.setItem('last-conversation-check', latest.toString());
    }

    setLoading(false);
  };

  // Check if email is flooring-related using keywords
  const isFlooringRelated = (conv: any): boolean => {
    const flooringKeywords = [
      'floor', 'flooring', 'hardwood', 'refinish', 'sand', 'stain',
      'laminate', 'vinyl', 'installation', 'plancher', 'bois franc',
      'wood', 'oak', 'maple', 'stairs', 'steps', 'quote', 'estimate'
    ];

    const searchText = (
      (conv.title || '') + ' ' +
      (conv.lastMessage || '') +
      (conv.metadata?.subject || '')
    ).toLowerCase();

    return flooringKeywords.some(keyword => searchText.includes(keyword));
  };

  // Check if sender matches any block rule
  const isBlocked = (senderEmail: string, senderName: string = ''): boolean => {
    if (!senderEmail) return false;

    const emailLower = senderEmail.toLowerCase();
    const nameLower = senderName.toLowerCase();
    const domain = senderEmail.split('@')[1]?.toLowerCase();

    return blockRules.some(rule => {
      switch (rule.type) {
        case 'exact':
          return emailLower === rule.value.toLowerCase();
        case 'domain':
          return domain === rule.value.toLowerCase();
        case 'pattern':
          return emailLower.includes(rule.value.toLowerCase()) ||
                 nameLower.includes(rule.value.toLowerCase());
        default:
          return false;
      }
    });
  };

  // Filter conversations based on view (archived/active), channel, etc.
  const filteredConversations = conversations.filter(conv => {
    // Filter by archived status
    const isArchived = conv.status === 'archived';
    if (showArchived && !isArchived) return false;
    if (!showArchived && isArchived) return false;

    // Check block rules
    const senderEmail = conv.participants?.[0]?.email || conv.participants?.[0]?.gmail || '';
    const senderName = conv.participants?.[0]?.name || '';
    if (isBlocked(senderEmail, senderName)) {
      return false;
    }

    // Smart filter for emails only
    if (smartFilterEnabled && conv.channel === 'gmail') {
      if (!isFlooringRelated(conv)) {
        return false;
      }
    }

    // Filter by channel in tab mode
    if (layoutMode === 'tabs' && selectedChannel !== 'all') {
      const channel = conv.channel || 'sms';
      if (channel !== selectedChannel) return false;
    }

    return true;
  });

  const formatLastFetch = (date: Date | null) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 10) return 'Just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ago`;
  };

  const handleImport = async () => {
    // Check if user has configured API key
    if (!userApiKey) {
      setImportResult({ success: false, message: 'Please configure your API key first' });
      setShowApiKeyModal(true);
      return;
    }

    setImporting(true);
    setImportResult(null);
    setImportProgress(null);

    try {
      const importer = new ConversationImporter(userApiKey);

      // Import all (50 is max per request)
      const result = await importer.importRecentConversations(50, (progress) => {
        setImportProgress(progress);
      });

      if (result.success) {
        setImportResult({
          success: true,
          message: `Successfully imported ${result.imported} conversations! ${result.skipped > 0 ? `(${result.skipped} skipped as duplicates)` : ''}`
        });
        // Reload conversations
        await loadConversations();
      } else {
        setImportResult({
          success: false,
          message: `Import completed with errors: ${result.imported} imported, ${result.failed} failed. ${result.errors.join(', ')}`
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        message: `Import failed: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handleGmailAuth = () => {
    if (!gmailApi.isAuthenticated()) {
      gmailApi.startAuth();
    } else {
      handleGmailImport();
    }
  };

  const handleGmailCallback = async (code: string) => {
    try {
      await gmailApi.completeAuth(code);
      setImportResult({
        success: true,
        message: 'Gmail connected successfully! Click "Import from Gmail" to fetch conversations.'
      });
    } catch (error) {
      setImportResult({
        success: false,
        message: `Gmail auth failed: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  };

  const handleGmailImport = async () => {
    setGmailImporting(true);
    setImportResult(null);

    try {
      const threads = await gmailApi.fetchRecentConversations(20);

      let imported = 0;
      let failed = 0;

      for (const thread of threads) {
        try {
          // Get first email in thread for metadata
          const firstEmail = thread[0];

          // Create or find client by email
          const { data: existingClient } = await flooringBotDb.supabase
            .from('clients')
            .select('id')
            .eq('gmail', firstEmail.from)
            .single();

          let clientId = existingClient?.id;

          if (!clientId) {
            const { data: newClient } = await flooringBotDb.supabase
              .from('clients')
              .insert({
                name: firstEmail.from.split('<')[0].trim() || firstEmail.from,
                gmail: firstEmail.from,
                email: firstEmail.from.match(/<(.+)>/)?.[1] || firstEmail.from
              })
              .select('id')
              .single();

            clientId = newClient?.id;
          }

          if (!clientId) {
            failed++;
            continue;
          }

          // Check if conversation exists
          const { data: existing } = await flooringBotDb.supabase
            .from('conversations')
            .select('id')
            .eq('channel_id', firstEmail.threadId)
            .eq('channel', 'gmail')
            .single();

          if (existing) {
            continue; // Skip duplicate
          }

          // Create conversation
          const { data: conversation } = await flooringBotDb.supabase
            .from('conversations')
            .insert({
              client_id: clientId,
              channel: 'gmail',
              channel_id: firstEmail.threadId,
              status: 'active',
              metadata: { subject: firstEmail.subject }
            })
            .select('id')
            .single();

          if (!conversation) {
            failed++;
            continue;
          }

          // Import all messages in thread
          const messages = thread.map(email => ({
            conversation_id: conversation.id,
            sender_type: email.from.includes(firstEmail.to) ? 'client' : 'user',
            content: email.body,
            external_id: email.id,
            metadata: {
              subject: email.subject,
              from: email.from,
              to: email.to,
              isRead: email.isRead
            },
            created_at: email.date.toISOString()
          }));

          await flooringBotDb.supabase
            .from('messages')
            .insert(messages);

          imported++;
        } catch (error) {
          console.error('Failed to import Gmail thread:', error);
          failed++;
        }
      }

      setImportResult({
        success: failed === 0,
        message: `Imported ${imported} Gmail conversations${failed > 0 ? `, ${failed} failed` : ''}`
      });

      await loadConversations();
    } catch (error) {
      setImportResult({
        success: false,
        message: `Gmail import failed: ${error instanceof Error ? error.message : String(error)}`
      });
    } finally {
      setGmailImporting(false);
    }
  };

  // Save API key to database (if authenticated) or localStorage
  const handleSaveApiKey = async () => {
    const key = prompt('Enter your JaPlanchers API Key:');
    if (!key || !key.trim()) return;

    const trimmedKey = key.trim();
    setUserApiKey(trimmedKey);
    setShowApiKeyModal(false);

    if (profile) {
      // Authenticated user - save to database
      const { error } = await updateProfile({
        preferences: {
          ...profile.preferences,
          japlanchers_api_key: trimmedKey
        }
      });

      if (error) {
        console.error('[Multi-Tenant DB] Failed to save API key:', error);
        setImportResult({ success: false, message: 'Failed to save API key to database' });
      } else {
        console.log('[Multi-Tenant DB] API key saved to database for user:', profile.email, '-', trimmedKey.substring(0, 8) + '...');
        setImportResult({ success: true, message: 'API key saved to your account!' });
      }
    } else {
      // Not authenticated - save to localStorage as fallback
      localStorage.setItem('japlanchers_api_key', trimmedKey);
      console.log('[Multi-Tenant LocalStorage] API key saved to browser:', trimmedKey.substring(0, 8) + '...');
      setImportResult({ success: true, message: 'API key saved to browser (will persist across devices if you sign in)' });
    }
  };

  // Clear API key from database or localStorage
  const handleClearApiKey = async () => {
    if (!confirm('Are you sure you want to remove your API key?')) return;

    setUserApiKey('');

    if (profile) {
      // Authenticated user - remove from database
      const { error } = await updateProfile({
        preferences: {
          ...profile.preferences,
          japlanchers_api_key: undefined
        }
      });

      if (error) {
        console.error('[Multi-Tenant DB] Failed to remove API key:', error);
        setImportResult({ success: false, message: 'Failed to remove API key from database' });
      } else {
        console.log('[Multi-Tenant DB] API key removed from database for user:', profile.email);
        setImportResult({ success: true, message: 'API key removed from your account' });
      }
    } else {
      // Not authenticated - remove from localStorage
      localStorage.removeItem('japlanchers_api_key');
      console.log('[Multi-Tenant LocalStorage] API key removed from browser');
      setImportResult({ success: true, message: 'API key removed from browser' });
    }
  };

  // Smart block handler with multiple options
  const handleBlockSender = (email: string, name: string = '') => {
    if (!email) {
      alert('No email address found for this sender');
      return;
    }

    const domain = email.split('@')[1];
    const nameParts = name.toLowerCase().split(/[\s\-_]+/).filter(p => p.length > 2);

    // Build options
    let options = `Choose how to block this sender:\n\n`;
    options += `1. Block exact email: ${email}\n`;
    options += `2. Block entire domain: @${domain}\n`;

    // Add pattern options if we can extract meaningful parts from name
    const patterns: string[] = [];
    if (nameParts.length > 0) {
      // Take first meaningful word from name as pattern
      const firstWord = nameParts[0];
      if (firstWord.length > 3 && !['gmail', 'yahoo', 'hotmail', 'outlook'].includes(firstWord)) {
        patterns.push(firstWord);
      }
    }

    if (patterns.length > 0) {
      patterns.forEach((pattern, idx) => {
        options += `${3 + idx}. Block pattern containing: "${pattern}"\n`;
      });
    }

    options += `\n0. Cancel\n\nEnter your choice (1-${2 + patterns.length} or 0):`;

    const choice = prompt(options);

    if (!choice || choice === '0') return;

    const choiceNum = parseInt(choice);
    let newRule: BlockRule | null = null;

    if (choiceNum === 1) {
      newRule = { type: 'exact', value: email };
    } else if (choiceNum === 2) {
      newRule = { type: 'domain', value: domain };
    } else if (choiceNum >= 3 && choiceNum < 3 + patterns.length) {
      newRule = { type: 'pattern', value: patterns[choiceNum - 3] };
    }

    if (newRule) {
      setBlockRules(prev => [...prev, newRule]);
      loadConversations();

      const ruleDesc =
        newRule.type === 'exact' ? `exact email: ${newRule.value}` :
        newRule.type === 'domain' ? `entire domain: @${newRule.value}` :
        `pattern: "${newRule.value}"`;

      alert(`✓ Blocked ${ruleDesc}\n\nAll matching conversations will be hidden.`);
    }
  };

  const selectedConversation = conversations.find(
    (conv) => conv.id === selectedConversationId
  );

  return (
    <div className="flex h-screen bg-[#0f0f0f]">
      <div className="w-80 border-r border-gray-800 flex flex-col">
        {/* Header with Import Button */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-white">Conversations</h1>
              {loading && (
                <div className="w-3.5 h-3.5 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Layout Toggle */}
              <div className="flex items-center gap-1 p-1 bg-[#1c2128] border border-[#30363d] rounded-lg">
                <button
                  onClick={() => setLayoutMode('dropdown')}
                  className={`p-1.5 rounded transition-colors ${
                    layoutMode === 'dropdown'
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                  title="Dropdown layout"
                >
                  <LayoutList className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setLayoutMode('tabs')}
                  className={`p-1.5 rounded transition-colors ${
                    layoutMode === 'tabs'
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                  title="Tab layout"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>

              {/* Smart Filter Toggle */}
              <button
                onClick={async () => {
                  setSmartFilterEnabled(!smartFilterEnabled);
                  await loadConversations();
                }}
                className={`p-1.5 border border-[#30363d] rounded-lg transition-colors ${
                  smartFilterEnabled
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-[#1c2128] text-gray-500 hover:text-gray-300'
                }`}
                title={smartFilterEnabled ? 'Smart filter ON (flooring only)' : 'Smart filter OFF'}
              >
                <Filter className="w-4 h-4" />
              </button>

              {/* Archived Toggle */}
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`p-1.5 border border-[#30363d] rounded-lg transition-colors ${
                  showArchived
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-[#1c2128] text-gray-500 hover:text-gray-300'
                }`}
                title={showArchived ? 'Show active' : 'Show archived'}
              >
                <ArchiveIcon className="w-4 h-4" />
              </button>

              {/* API Key Settings */}
              <button
                onClick={userApiKey ? handleClearApiKey : handleSaveApiKey}
                className={`p-1.5 border border-[#30363d] rounded-lg transition-colors ${
                  userApiKey
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : 'bg-orange-500/20 text-orange-400 border-orange-500/30 animate-pulse'
                }`}
                title={userApiKey ? 'API key configured (click to remove)' : 'Configure API key (required)'}
              >
                <Key className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* API Key Warning */}
          {!userApiKey && (
            <div className="mb-3 p-2 bg-orange-500/10 border border-orange-500/30 rounded-lg text-xs text-orange-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium mb-1">API Key Required</p>
                <p className="text-orange-400/80">Click the key icon above to configure your personal API key for SMS imports.</p>
              </div>
            </div>
          )}

          {/* Channel Tabs (only in tab mode) */}
          {layoutMode === 'tabs' && !showArchived && (
            <div className="flex gap-1 mb-3 overflow-x-auto pb-2 custom-scrollbar">
              <button
                onClick={() => setSelectedChannel('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                  selectedChannel === 'all'
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    : 'bg-[#1c2128] text-gray-400 border border-[#30363d] hover:bg-[#21262d]'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedChannel('sms')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                  selectedChannel === 'sms'
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    : 'bg-[#1c2128] text-gray-400 border border-[#30363d] hover:bg-[#21262d]'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                SMS
              </button>
              <button
                onClick={() => setSelectedChannel('gmail')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                  selectedChannel === 'gmail'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'bg-[#1c2128] text-gray-400 border border-[#30363d] hover:bg-[#21262d]'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                Email
              </button>
              <button
                onClick={() => setSelectedChannel('instagram')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                  selectedChannel === 'instagram'
                    ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                    : 'bg-[#1c2128] text-gray-400 border border-[#30363d] hover:bg-[#21262d]'
                }`}
              >
                <Instagram className="w-3.5 h-3.5" />
                Instagram
              </button>
              <button
                onClick={() => setSelectedChannel('facebook')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                  selectedChannel === 'facebook'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'bg-[#1c2128] text-gray-400 border border-[#30363d] hover:bg-[#21262d]'
                }`}
              >
                <Facebook className="w-3.5 h-3.5" />
                Messenger
              </button>
            </div>
          )}

          {/* Status Lights */}
          <div className="space-y-2">
            {/* SMS Status */}
            <div className="flex items-center justify-between p-2 bg-[#1c2128] border border-[#30363d] rounded-lg">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-cyan-500" />
                <span className="text-xs font-medium text-gray-300">SMS</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{formatLastFetch(smsLastFetch)}</span>
                <div className={`w-2 h-2 rounded-full ${
                  smsFetching ? 'bg-yellow-500 animate-pulse' :
                  smsLastFetch ? 'bg-green-500' : 'bg-gray-500'
                }`} />
              </div>
            </div>

            {/* Gmail Status */}
            <div
              onClick={() => !gmailApi.isAuthenticated() && handleGmailAuth()}
              className={`flex items-center justify-between p-2 bg-[#1c2128] border border-[#30363d] rounded-lg ${
                !gmailApi.isAuthenticated() ? 'cursor-pointer hover:bg-[#21262d] transition-colors' : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-red-500" />
                <span className="text-xs font-medium text-gray-300">Gmail</span>
                {!gmailApi.isAuthenticated() && (
                  <span className="text-xs text-gray-500">(click to connect)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {gmailApi.isAuthenticated() ? formatLastFetch(gmailLastFetch) : 'Not connected'}
                </span>
                <div className={`w-2 h-2 rounded-full ${
                  gmailFetching ? 'bg-yellow-500 animate-pulse' :
                  gmailLastFetch ? 'bg-green-500' :
                  gmailApi.isAuthenticated() ? 'bg-gray-500' : 'bg-red-500'
                }`} />
              </div>
            </div>
          </div>

          {/* Import Progress */}
          {importProgress && (
            <div className="mt-3 p-2 bg-[#1c2128] border border-[#30363d] rounded text-xs">
              <div className="flex items-center justify-between text-gray-400 mb-1">
                <span>Progress:</span>
                <span>{importProgress.processed} / {importProgress.total}</span>
              </div>
              <div className="w-full bg-[#0d1117] rounded-full h-1.5 mb-2">
                <div
                  className="bg-cyan-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${(importProgress.processed / importProgress.total) * 100}%` }}
                />
              </div>
              {importProgress.currentConversation && (
                <div className="text-gray-500 truncate">
                  {importProgress.currentConversation}
                </div>
              )}
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className={`mt-3 p-2 rounded text-xs flex items-start gap-2 ${
              importResult.success
                ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`}>
              {importResult.success ? (
                <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              )}
              <span className="flex-1">{importResult.message}</span>
              <button
                onClick={() => setImportResult(null)}
                className="text-gray-400 hover:text-gray-300"
              >
                ×
              </button>
            </div>
          )}
        </div>

        <ConversationList
          conversations={filteredConversations}
          selectedId={selectedConversationId}
          onSelect={setSelectedConversationId}
          onConversationUpdate={loadConversations}
          layoutMode={layoutMode}
          onBlockSender={(email, name) => handleBlockSender(email, name)}
        />
      </div>

      <div className="flex-1 flex">
        {selectedConversation ? (
          <>
            <div className="flex-1 max-w-3xl">
              <MessageThread conversation={selectedConversation} />
            </div>
            {/* Sidebar */}
            <div className="w-96 border-l border-gray-800 bg-[#0d1117] flex flex-col p-4 space-y-4">
              {/* Calendar Card */}
              <div className="bg-[#1c2128] border border-[#30363d] rounded-lg overflow-hidden h-[480px] flex flex-col">
                <div className="p-3 border-b border-[#30363d]">
                  <h2 className="text-sm font-semibold text-white">Schedule</h2>
                </div>
                <div className="flex-1 min-h-0">
                  <Calendar events={calendarEvents} />
                </div>
              </div>

              {/* Client Info Card */}
              <div className="bg-[#1c2128] border border-[#30363d] rounded-lg p-4">
                <h3 className="text-sm font-semibold text-white mb-3">Client Info</h3>
                <div className="space-y-3 text-xs">
                  <div>
                    <span className="text-gray-500 block mb-1">Name</span>
                    <p className="text-gray-300 font-medium">{selectedConversation.title}</p>
                  </div>
                  {selectedConversation.participants?.[0]?.email && (
                    <div>
                      <span className="text-gray-500 block mb-1">Email</span>
                      <a
                        href={`mailto:${selectedConversation.participants[0].email}`}
                        className="text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        {selectedConversation.participants[0].email}
                      </a>
                    </div>
                  )}
                  {selectedConversation.participants?.[0]?.phone && (
                    <div>
                      <span className="text-gray-500 block mb-1">Phone</span>
                      <a
                        href={`tel:${selectedConversation.participants[0].phone}`}
                        className="text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        {selectedConversation.participants[0].phone}
                      </a>
                    </div>
                  )}
                  {selectedConversation.metadata?.subject && (
                    <div>
                      <span className="text-gray-500 block mb-1">Subject</span>
                      <p className="text-gray-300">{selectedConversation.metadata.subject}</p>
                    </div>
                  )}
                </div>

                {/* Test in AI button */}
                <a
                  href={`/dashboard/ai-testing?conversation=${selectedConversation.id}`}
                  className="mt-3 w-full px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600
                           hover:from-purple-500 hover:to-pink-500
                           text-white text-sm font-medium rounded-lg
                           flex items-center justify-center gap-2 transition-all
                           shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
                >
                  <Sparkles className="w-4 h-4" />
                  Test in AI
                </a>
              </div>
            </div>
          </>
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
}
